import React, { useEffect, useRef, useState } from 'react';
import {
    CreateMedicalOpinionFileInput,
    MedicalOpinionType,
} from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { MedicalOpinionFilePreview } from '../medicalOpinion/MedicalOpinionFilePreview';
import { MedicalOpinionFileRow } from '../medicalOpinion/MedicalOpinionFileRow';
import { downloadObjectUrl } from '../medicalOpinion/medicalOpinionFiles';
import { getTextDirection } from '../../utils/textDirection';
import { MODAL_WIDTHS, POPUP_STYLES } from '../../constants/popupStyles';
import {
    MEDICAL_OPINION_ACCEPTED_MIME as ACCEPTED_MIME,
    MEDICAL_OPINION_ACCEPT_ATTR as ACCEPT_ATTR,
    MEDICAL_OPINION_MAX_FILES as MAX_FILES,
    MEDICAL_OPINION_MAX_FILE_BYTES as MAX_FILE_BYTES,
} from '../../constants/medicalOpinion';
import { useMedicalOpinionTypes } from '../../hooks/useMedicalOpinionOptions';

interface CreateMedicalOpinionModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Read-only context, so the user can confirm who the request is being opened for
    // before sending it.
    patientName: string;
    patientTz: string;
    patientHmId: string;
    clinicName: string;
    doctorName: string;
    onCreate: (data: {
        opinion_type: MedicalOpinionType;
        obligation: number;
        initiator_notes: string;
        files: CreateMedicalOpinionFileInput[];
    }) => Promise<void>;
    isSaving?: boolean;
    // Business-rule rejection from the backend ({status:"error", message}), shown in the
    // same place as client-side validation errors.
    serverError?: string | null;
}

export const CreateMedicalOpinionModal: React.FC<CreateMedicalOpinionModalProps> = ({
    isOpen,
    onClose,
    patientName,
    patientTz,
    patientHmId,
    clinicName,
    doctorName,
    onCreate,
    isSaving = false,
    serverError = null,
}) => {
    // Served by the backend and cached for the session, so opening the modal repeatedly
    // costs one request in total.
    const {
        options: opinionTypeOptions,
        isLoading: isLoadingOpinionTypes,
        isError: opinionTypesFailed,
    } = useMedicalOpinionTypes();

    const [opinionType, setOpinionType] = useState<MedicalOpinionType | ''>('');
    const [obligation, setObligation] = useState('');
    const [notes, setNotes] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Preview of a locally selected file, before it is uploaded. Object URLs are revoked on
    // replacement and on unmount so picking through several files does not leak Blobs.
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState<number | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const previewUrlRef = useRef<string | null>(null);

    const setPreview = (url: string | null) => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
    };

    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
        };
    }, []);

    if (!isOpen) return null;

    const reset = () => {
        setOpinionType('');
        setObligation('');
        setNotes('');
        setFiles([]);
        setError(null);
        setSelectedPreviewIndex(null);
        setPreview(null);
    };

    // Clicking the selected file again closes the preview, so the pane can be dismissed
    // without removing the attachment.
    const togglePreview = (index: number) => {
        const file = files[index];
        if (!file) return;
        if (selectedPreviewIndex === index) {
            setSelectedPreviewIndex(null);
            setPreview(null);
            return;
        }
        setSelectedPreviewIndex(index);
        setPreview(URL.createObjectURL(file));
    };

    const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files || []);
        // Clear the input value so re-picking the same file after a removal still fires
        // onChange.
        e.target.value = '';
        if (!picked.length) return;

        const next = [...files];
        for (const file of picked) {
            if (next.length >= MAX_FILES) {
                setError(`No more than ${MAX_FILES} files per request`);
                break;
            }
            if (file.size > MAX_FILE_BYTES) {
                setError(`"${file.name}" is larger than ${MAX_FILE_BYTES / (1024 * 1024)}MB`);
                continue;
            }
            // A browser reports an empty type for some files; let those through and leave
            // the real check to the backend's content sniffing.
            if (file.type && !ACCEPTED_MIME.includes(file.type)) {
                setError(`"${file.name}" must be a PDF, JPEG or PNG`);
                continue;
            }
            if (next.some((f) => f.name === file.name && f.size === file.size)) {
                continue;
            }
            next.push(file);
        }
        setFiles(next);

        // Select the last file that was actually added and open its preview, so the user sees
        // what they just attached without a second click.
        if (next.length > files.length) {
            const addedIndex = next.length - 1;
            setSelectedPreviewIndex(addedIndex);
            setPreview(URL.createObjectURL(next[addedIndex]));
        }
    };

    // Straight from the picked File - it never went to the server, so there is nothing to
    // fetch. Same icon and same behaviour as the two modals that show stored files.
    const downloadLocalFile = (index: number) => {
        const file = files[index];
        if (!file) return;
        const url = URL.createObjectURL(file);
        downloadObjectUrl(url, file.name);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    const removeFile = (index: number) => {
        setFiles(files.filter((_, i) => i !== index));
        setError(null);
        // Indexes shift on removal, so drop the preview rather than let it point at the
        // wrong file.
        if (selectedPreviewIndex !== null) {
            setSelectedPreviewIndex(null);
            setPreview(null);
        }
    };

    const toBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                // Strip the data URL prefix - the backend expects bare base64.
                resolve(result.split(',')[1]);
            };
            reader.onerror = (err) => reject(err);
        });
    };

    const handleCreate = async () => {
        // Checked in the order the fields are shown, so the message points at the first gap
        // the user would see rather than jumping down the form.
        if (!opinionType) {
            setError('Please select an opinion type');
            return;
        }
        if (!notes.trim()) {
            setError('Please describe the request');
            return;
        }
        // The obligation number is mandatory. It can still be corrected later at the
        // obligation approval step, but a request is not opened without one.
        if (!obligation.trim()) {
            setError('Please enter the obligation number');
            return;
        }
        if (!/^\d+$/.test(obligation.trim())) {
            setError('Obligation must be a number');
            return;
        }

        try {
            setError(null);
            const encoded: CreateMedicalOpinionFileInput[] = [];
            for (const file of files) {
                encoded.push({ filename: file.name, base64file: await toBase64(file) });
            }
            await onCreate({
                opinion_type: opinionType,
                obligation: Number(obligation.trim()),
                initiator_notes: notes,
                files: encoded,
            });
            // The parent closes the modal on success; resetting here would blank the form
            // under a visible error if the request was rejected.
        } catch (err: any) {
            setError(err?.message || 'Failed to open the request');
        }
    };

    const handleCancel = () => {
        reset();
        onClose();
    };

    const shownError = error || serverError;
    const isPreviewOpen = selectedPreviewIndex !== null;
    const previewFile = isPreviewOpen ? files[selectedPreviewIndex as number] : null;

    return (
        <ModalWrapper modalId="CreateMedicalOpinionModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Medical Opinion Request"
            isLoading={isSaving}
            // The modal grows sideways to make room for the preview rather than resizing the
            // form: max-w-lg is 32rem, less the 2rem of px-8 either side, so the left column
            // is 28rem either way. The pane is 1.5x that, so open width =
            // 28 + gap 1.5 + 42 + 4 padding = 75.5rem.
            width={isPreviewOpen ? 'max-w-[76rem]' : MODAL_WIDTHS.wide}
        >
            {/* Subtitle sits above the columns, so the preview pane starts level with the
                patient details rather than below them. */}
            <p className="text-sm text-[#366A75] mb-4">
                Open a psychiatric medical opinion request for this patient.
            </p>

            <div className={isPreviewOpen ? 'flex gap-6 items-stretch' : ''}>
            <div className={isPreviewOpen ? 'w-[28rem] shrink-0 flex flex-col' : ''}>
            <div className="space-y-4 mb-6">

                {/* Patient context (read only) - same header block style as the
                    Case Closure Approval modal. */}
                <div className="flex gap-x-6 text-base text-[#366A75]">
                    {/* Left column */}
                    <div className="flex flex-col gap-y-1 flex-1">
                        <div><span className="font-bold">Name:</span> {patientName || '—'}</div>
                        <div><span className="font-bold">Id (TZ):</span> {patientTz || '—'}</div>
                        <div><span className="font-bold">HM_ID:</span> {patientHmId || '—'}</div>
                    </div>
                    {/* Right column */}
                    <div className="flex flex-col gap-y-1 flex-1">
                        <div><span className="font-bold">Clinic:</span> {clinicName || '—'}</div>
                        <div><span className="font-bold">Doctor:</span> {doctorName || '—'}</div>
                    </div>
                </div>

                {/* Opinion Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Opinion Type <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={opinionType}
                        onChange={(e) => setOpinionType(e.target.value as MedicalOpinionType)}
                        className={POPUP_STYLES.inputBordered}
                        disabled={isSaving || isLoadingOpinionTypes || opinionTypesFailed}
                    >
                        <option value="">
                            {isLoadingOpinionTypes
                                ? 'Loading opinion types...'
                                : 'Select an opinion type'}
                        </option>
                        {opinionTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {/* Without the list there is nothing valid to submit, so this is said
                        here rather than left to a rejection after the fact. */}
                    {opinionTypesFailed && (
                        <p className="mt-1 text-xs text-red-600">
                            Could not load the opinion types. Please reload the page.
                        </p>
                    )}
                </div>

                {/* Request details - stored as initiator_notes */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Request Details <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        // Follows the content, so the field flips once the first Hebrew letter
                        // is typed and stays LTR for English.
                        dir={getTextDirection(notes)}
                        className={`${POPUP_STYLES.inputBordered} resize-none`}
                        placeholder="Place a short description of the request"
                        disabled={isSaving}
                    />
                </div>

                {/* Obligation */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Obligation Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={obligation}
                        onChange={(e) => setObligation(e.target.value)}
                        className={POPUP_STYLES.inputBordered}
                        placeholder="Place the obligation number as received from the clinic"
                        disabled={isSaving}
                    />
                </div>

                {/* Files. The picker is the + beside the caption; a label pointing at a
                    disabled input does not open the file dialog, so the limit is enforced by
                    disabling the input itself. */}
                <div>
                    <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-medium text-gray-700">
                            Add Files <span className="text-xs font-normal text-gray-500">
                                (up to {MAX_FILES} files, PDF / JPEG / PNG, max {MAX_FILE_BYTES / (1024 * 1024)}MB each)
                            </span>
                        </span>
                        <input
                            type="file"
                            multiple
                            accept={ACCEPT_ATTR}
                            onChange={handleFilesChange}
                            className="hidden"
                            id="medical-opinion-files"
                            disabled={isSaving || files.length >= MAX_FILES}
                        />
                        <label
                            htmlFor="medical-opinion-files"
                            className={`shrink-0 rounded-lg border-[1px] border-[#B4D9D4] bg-[#EBF5F5] text-[#366A75] p-1 leading-none ${
                                isSaving || files.length >= MAX_FILES
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'cursor-pointer hover:bg-[#D0E7E5]'
                            }`}
                            title={
                                files.length >= MAX_FILES
                                    ? `${MAX_FILES} of ${MAX_FILES} files added`
                                    : 'Add files'
                            }
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </label>
                    </div>

                    {files.length > 0 && (
                        <ul className="mt-2 space-y-1">
                            {files.map((file, index) => (
                                <MedicalOpinionFileRow
                                    key={`${file.name}-${file.size}`}
                                    filename={file.name}
                                    isSelected={selectedPreviewIndex === index}
                                    onPreview={() => togglePreview(index)}
                                    onDownload={() => downloadLocalFile(index)}
                                    onRemove={() => removeFile(index)}
                                    disabled={isSaving}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {shownError && (
                <div className="mb-4 p-3 bg-red-100 border-[1px] border-red-400 text-red-700 rounded-lg text-sm">
                    {shownError}
                </div>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <button onClick={handleCreate} disabled={isSaving} className={POPUP_STYLES.button}>
                    {isSaving ? 'Opening...' : 'Open Request'}
                </button>
                <button onClick={handleCancel} disabled={isSaving} className={POPUP_STYLES.button}>
                    Cancel
                </button>
            </div>
            </div>

            {/* Right side - file preview. Rendered only while a file is selected; clicking the
                same file again closes it, which is why there is no header or close control
                here. items-stretch on the row makes this match the form's height exactly, so
                the pane runs from the patient details down to the buttons. Width is 1.5x the
                form: 28rem -> 42rem. */}
            {isPreviewOpen && (
                <div className="w-[42rem] shrink-0 flex flex-col border-l border-gray-200 pl-6">
                    <MedicalOpinionFilePreview
                        src={previewUrl}
                        contentType={previewFile?.type || null}
                        filename={previewFile?.name || null}
                        heightClass="flex-1 min-h-0"
                    />
                </div>
            )}
            </div>
        </ModalWrapper>
    );
};
